import { getRouteApi } from '@tanstack/react-router'
import { debounce } from 'es-toolkit'
import type { TransitionSpecification } from 'mapbox-gl'
import { useEffect, useState } from 'react'
import type { MeasurementTypeUnit } from '../../data/measurementTypes.ts'
import type { UserPreferences } from '../../data/userProfile.ts'
import { useMap } from '../../states.ts'

export const eventsPerYearLongUnit: Record<UserPreferences['unitsDistance'], MeasurementTypeUnit> = {
	KILOMETRES: 'EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_LONG',
	MILES: 'EVENTS_PER_SQUARE_MILES_PER_YEAR_LONG',
	NAUTICAL_MILES: 'EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_LONG',
}

export const eventsPerYearShortUnit: Record<UserPreferences['unitsDistance'], MeasurementTypeUnit> = {
	KILOMETRES: 'EVENTS_PER_SQUARE_KILOMETRE_PER_YEAR_SHORT',
	MILES: 'EVENTS_PER_SQUARE_MILES_PER_YEAR_SHORT',
	NAUTICAL_MILES: 'EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_YEAR_SHORT',
}

export const eventsPerHourShortUnit: Record<UserPreferences['unitsDistance'], MeasurementTypeUnit> = {
	KILOMETRES: 'EVENTS_PER_SQUARE_KILOMETRE_PER_HOUR_SHORT',
	MILES: 'EVENTS_PER_SQUARE_MILES_PER_HOUR_SHORT',
	NAUTICAL_MILES: 'EVENTS_PER_SQUARE_NAUTICAL_MILES_PER_HOUR_SHORT',
}

export const squareAreaUnit: Record<UserPreferences['unitsDistance'], MeasurementTypeUnit> = {
	KILOMETRES: 'KILOMETRES_SQUARED',
	MILES: 'MILES_SQUARED',
	NAUTICAL_MILES: 'NAUTICAL_MILES_SQUARED',
}

export const lightningLayerIcons = {
	positive: 'lightning-plus',
	negative: 'lightning-minus',
}

export const useIsMapZoomBelow = (zoomCutoff: number, opts: { debounce: boolean } = { debounce: true }) => {
	const [show, setShow] = useState(false)
	const map = useMap((s) => s.map)

	useEffect(() => {
		if (!map) return
		const onZoom = () => {
			const zoom = map.getZoom()
			setShow(zoom < zoomCutoff)
		}

		const debouncedFn = opts.debounce ? debounce(onZoom, 10) : undefined
		const wrappedOnZoom = debouncedFn ?? onZoom
		wrappedOnZoom()
		map.on('zoom', wrappedOnZoom)
		return () => {
			map.off('zoom', wrappedOnZoom)
			debouncedFn?.cancel()
		}
	}, [map, zoomCutoff, opts.debounce])

	return show
}

export const layerStyleNoTransition: TransitionSpecification = { delay: 0, duration: 0 }

const route = getRouteApi('/_app')

export const useActiveLayerIds = (): string[] => route.useSearch({ select: (s) => s.layers ?? [] })

export const useIsLayerActive = (...ids: string[]): boolean => {
	const layers = useActiveLayerIds()
	return ids.some((id) => layers.includes(id))
}
